// AI-CONTEXT: See docs/methodiek-impact-per-euro.md § 1.1 en § 1.3
// AI-CONTEXT: See docs/plans/ondersteuningsmodule-code-integratie.md (T3)
import { useMemo } from 'react'
import { useFirestore, useFirestoreQuery } from '../firebase/useFirestore'
import useInterventieLibrary from './useInterventieLibrary'
import sumMaterialPeriodValue from '../helpers/materialsHelpers/sumMaterialPeriodValue'
import { HOUSEHOLD_FIELDS, residentCountsByAdress } from '../helpers/adressHelpers/householdFields'
import notDeleted from '../helpers/notDeleted'

/**
 * De materialen-waardestroom: de maatschappelijke besparing van geregistreerde
 * materialen en coachadviezen, optioneel begrensd op een periode.
 *
 * Zelfde vorm als `useMsiMonetaryTotal`: mét grenzen levert het de
 * boekjaar-teller van Impact per euro, zónder grenzen het live totaal voor de
 * kaart Maatschappelijke waarde. Eén hook voor beide, zodat de twee getallen
 * nooit op verschillende regels uitkomen.
 *
 * ALLEEN DE MAATSCHAPPELIJKE BESPARING. De besparing op de energierekening en
 * de bespaarde CO2 zijn MSI-outputs met een eigen eenheid en horen in de
 * benchmark, niet in de teller. `sumMaterialPeriodValue` geeft ze niet eens
 * terug — zie methodiek § 1.3.
 *
 * NULL VERSUS NUL, en waarom dat verschil hier zwaar weegt:
 *
 *   null — nog aan het laden, of de bibliotheek ontbreekt terwijl er wél
 *          registraties zijn. Er is dan geen kengetal; een teller met twee van
 *          de drie stromen doet zich als compleet voor.
 *   0    — er is niets te tellen: geen registraties, of niets in dit boekjaar.
 *          Dat is een feit, geen gat.
 *
 * Die tweede regel is er voor de meeste organisaties: wie geen materialen
 * registreert mag haar kengetal niet kwijtraken zodra de meetstandaard hapert.
 *
 * ONMEETBAAR BLOKKEERT HET TOTAAL NIET. Een materiaal zonder omrekenfactor of
 * een huishouden zonder bewonersaantal valt uit de som en komt terug in
 * `unknownCount`. De dekkingsindicator meldt dat (§ 6.3); het hele kengetal
 * laten verdwijnen om één ontbrekend veld zou strenger zijn dan de methodiek
 * vraagt.
 *
 * BEWONERS: het vaste veld `AantalBewoners` op het huishouden, met de oude
 * PersonaData-regel als terugval tot de migratie. Die regels worden sinds T8
 * met een exacte titelquery gelezen in plaats van heel PersonaData. Dat mist
 * schrijfvarianten ('aantal bewoners'), en daar was de brede lees voor — maar
 * nagemeten 2026-10-28 waren alle 175 regels exact 'Aantal bewoners', en sinds
 * T4 kan die titel niet meer als eigen gegeven ontstaan.
 * AI-CONTEXT: docs/pitfalls.md#database-firestore-silent-name-mismatch
 * AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T8)
 *
 * @param {{startMs?: number, endMs?: number}} [bounds] - Periodegrenzen in epoch-ms; weglaten telt alles
 * @returns {{ value: number|null, loading: boolean, unknownCount: number, householdCount: number, error: Error|null }}
 *   `loading` scheidt 'nog niet bekend' van 'niets te tonen': beide geven
 *   `value: null`, maar alleen het tweede mag als streepje op het scherm.
 *   AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 */
/**
 * Alleen de oude bewonersregels, niet heel PersonaData: tot T8 opende deze hook
 * een listener op alle gegevensregels van de organisatie (bij de grootste ~4.900)
 * om één titel te lezen. Een vaste array, zodat de query niet elke render opnieuw begint.
 * AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T8, bug 8)
 */
const RESIDENT_TITLES = HOUSEHOLD_FIELDS.AantalBewoners.legacyTitles
const RESIDENT_QUERY = { where: [['Title', 'in', RESIDENT_TITLES]] }

const useMaterialValue = ({ startMs, endMs } = {}) => {
    const entries = useFirestore('AdressMaterials')
    // Óók de zacht verwijderde materialen: hun registraties zijn gebeurd en
    // tellen mee, net als op het scherm van het huishouden (inbox #284).
    const allMaterials = useFirestoreQuery('Materials', {
        orderBy: ['Position', 'asc'],
        includeDeleted: true,
    })
    const personaData = useFirestoreQuery('PersonaData', RESIDENT_QUERY)
    // Het aantal bewoners als vast gegeven op het huishouden.
    const adresses = useFirestore('Adresses')
    const { library, loading: libraryLoading, error } = useInterventieLibrary()

    // De hooks geven '' terug zolang ze laden; een lege array is een geladen
    // leeg resultaat en betekent iets heel anders. De bibliotheek telt mee:
    // zonder haar is er geen bedrag, en dat is 'nog niet bekend' zolang ze
    // onderweg is — niet 'geen waarde'.
    const loading = entries === '' || allMaterials === '' || personaData === '' || adresses === '' || libraryLoading === true

    const liveEntries = useMemo(
        () => (Array.isArray(entries) ? entries.filter(notDeleted) : []),
        [entries]
    )

    const residentsByAdress = useMemo(() => {
        if (!Array.isArray(personaData) || !Array.isArray(adresses)) return {}
        return residentCountsByAdress(adresses, personaData)
    }, [personaData, adresses])

    return useMemo(() => {
        const leeg = { value: null, loading, unknownCount: 0, householdCount: 0, error: error || null }

        if (loading) return leeg

        // Niets geregistreerd: de stroom draagt nul bij, ook zonder bibliotheek.
        if (!liveEntries.length) return { ...leeg, value: 0 }

        if (!library?.byCode) return leeg

        const result = sumMaterialPeriodValue({
            entries: liveEntries,
            materials: Array.isArray(allMaterials) ? allMaterials : [],
            library,
            residentsByAdress,
            startMs,
            endMs,
        })

        return {
            // null uit de helper betekent 'niets berekenbaar'. Op tellerniveau
            // is dat een bijdrage van nul: de gaten reizen mee in unknownCount.
            value: result.societalEur ?? 0,
            loading: false,
            unknownCount: result.unknownCount,
            householdCount: result.householdCount,
            error: error || null,
        }
    }, [loading, startMs, endMs, liveEntries, allMaterials, library, residentsByAdress, error])
}

export default useMaterialValue
